/** Alinhado ao backend: weekday Python 0=segunda … 3=quinta. */
export const DEFAULT_CHANGE_WINDOW_WEEKDAY = 3;
export const DEFAULT_RISK_COMMITTEE_WEEKDAY = 1;
export const DEFAULT_CAMUD_WEEKDAY = 3;

export const WEEKDAY_LABELS: Record<number, string> = {
  0: 'segunda-feira',
  1: 'terça-feira',
  2: 'quarta-feira',
  3: 'quinta-feira',
  4: 'sexta-feira',
  5: 'sábado',
  6: 'domingo',
};

export function weekdayName(weekday?: number | null): string | null {
  if (weekday == null) return null;
  return WEEKDAY_LABELS[weekday] || null;
}

export function nativeWeekdayConstraint(
  fields:
    | Array<{ native_key?: string | null; weekday_constraint?: number | null }>
    | null
    | undefined,
  nativeKey: string,
  fallback: number | null,
): number | null {
  const field = (fields || []).find((item) => item.native_key === nativeKey);
  if (!field) return fallback;
  if (field.weekday_constraint === undefined) return fallback;
  return field.weekday_constraint;
}

/** Alinhado ao backend `REGULAR_GO_LIVE_TYPE_CODES` (change_type_policies). */
const REGULAR_GO_LIVE_TYPE_CODES = new Set(['normal', 'normal_correcao', 'padrao']);

export function requiresThursdayGoLive(changeType: string): boolean {
  return REGULAR_GO_LIVE_TYPE_CODES.has(changeType);
}

/** `datetime-local` / Date local → weekday Python (0=segunda). */
export function localDateToPythonWeekday(value: string | Date): number {
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return -1;
  // JS getDay: 0=domingo … 6=sábado → Python: 0=segunda … 6=domingo
  return (date.getDay() + 6) % 7;
}

export function isGoLiveOnWindowDay(
  value: string | Date,
  weekday: number | null = DEFAULT_CHANGE_WINDOW_WEEKDAY,
): boolean {
  if (weekday == null) return true;
  return localDateToPythonWeekday(value) === weekday;
}

/** Campo `date` YYYY-MM-DD sem passar por UTC. */
export function dateOnlyToPythonWeekday(value: string): number {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return -1;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(date.getTime())) return -1;
  return (date.getDay() + 6) % 7;
}

export function isDateOnWeekday(
  value: string,
  weekday: number | null | undefined,
  dateOnly = true,
): boolean {
  if (weekday == null) return true;
  const actual = dateOnly ? dateOnlyToPythonWeekday(value) : localDateToPythonWeekday(value);
  return actual === weekday;
}

export function isRiskCommitteeOnTuesday(value: string): boolean {
  return isDateOnWeekday(value, DEFAULT_RISK_COMMITTEE_WEEKDAY);
}

export function isCamudOnThursday(value: string): boolean {
  return isDateOnWeekday(value, DEFAULT_CAMUD_WEEKDAY);
}

export function formatGovernanceWindow(start?: string | null, end?: string | null): string {
  const startText = formatGovernanceDate(start);
  const endText = formatGovernanceDate(end);
  if (startText === '—' && endText === '—') return '—';
  if (startText === endText) return startText;
  return `${startText} → ${endText}`;
}

/**
 * `datetime-local` → string enviada ao backend, sem converter para UTC.
 *
 * O backend guarda datas em horário de Brasília sem fuso. Enviando
 * `toISOString()` o instante ia em UTC e voltava 3h adiantado, além de jogar
 * um Go Live de quinta à noite para sexta e derrubar o gate da janela.
 */
export function toBackendDateTime(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

export function toLocalDateTimeInput(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

/**
 * Valor de um campo `date` (sem hora) vindo do backend.
 *
 * O backend serializa como `YYYY-MM-DD`; passar isso por `new Date()` o
 * interpretaria como meia-noite UTC e, no fuso de Brasília, exibiria o dia
 * anterior. Por isso o prefixo é extraído do texto.
 */
export function toLocalDateInput(value: string | null | undefined): string {
  const match = String(value || '').match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

/**
 * Janela de Go Live em uma linha: "02/07/2026 20:00 → 23:00".
 *
 * Quando início e fim caem em dias diferentes a data se repete no fim, senão
 * só a hora aparece — a versão longa polui a coluna da listagem.
 */
export function formatGoLiveWindow(start: string, end?: string | null): string {
  const startDate = new Date(start);
  if (Number.isNaN(startDate.getTime())) return '—';

  const startText = startDate.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const endDate = end ? new Date(end) : null;
  if (!endDate || Number.isNaN(endDate.getTime())) return startText;

  const sameDay = startDate.toDateString() === endDate.toDateString();
  const endText = endDate.toLocaleString(
    'pt-BR',
    sameDay
      ? { hour: '2-digit', minute: '2-digit' }
      : { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' },
  );
  return `${startText} → ${endText}`;
}

/** Data sem hora para exibição (campos de agendamento de governança). */
export function formatGovernanceDate(value?: string | null): string {
  const iso = toLocalDateInput(value);
  if (!iso) return '—';
  const [year, month, day] = iso.split('-');
  return `${day}/${month}/${year}`;
}

/** Data e hora sem passar por UTC: `2026-10-13T14:50` → `13/10/2026, 14:50`. */
export function formatDateTimeLabel(value?: string | null): string {
  const text = String(value || '').trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (match) {
    return `${match[3]}/${match[2]}/${match[1]}, ${match[4]}:${match[5]}`;
  }
  const dateOnly = toLocalDateInput(text);
  if (dateOnly) return formatGovernanceDate(dateOnly);
  return text || '—';
}

/** Extrai YYYY-MM-DD estável do ISO (evita shift de timezone no calendário). */
export function isoDateKey(iso: string): string {
  const match = String(iso || '').match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}
